// src/components/editor/TemplateMenu.jsx
//
// The template switcher behind the Template button in the editor's format bar:
// every catalogue template by category, the current one ticked, and — when the
// last change is on record — a way to undo it.
//
// Picking one only reports the choice. Switching means freezing the text that
// is already there as regular text, which the editor asks about first and does
// itself (src/utils/templateOrphan.js); undo is the editor's too.
import React, { useEffect, useState } from 'react';
import { Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';

import { tokens } from '../../theme';
import { listTemplates } from '../../utils/manuscriptTemplateApi';
import { orderedCategoriesFor } from '../templates/templateTokens';

// The app's menus are purple (its MUI theme), so everything is read on a dark
// ground — the same look as the theme menu beside it.
const subheaderSx = {
  fontFamily: tokens.family.sans,
  fontSize: tokens.text.micro.size,
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.color.onDark,
  background: 'transparent',
  lineHeight: '28px',
};

export default function TemplateMenu({
  anchorEl, onClose, currentId, undoName, canUndo = false, onPick, onUndo,
}) {
  const open = Boolean(anchorEl);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open || templates.length) return undefined;
    let cancelled = false;
    listTemplates()
      .then((list) => { if (!cancelled && Array.isArray(list)) setTemplates(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, templates.length]);

  const groups = orderedCategoriesFor(templates).map((c) => ({
    ...c, items: templates.filter((t) => t.category === c.id),
  }));

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 320, maxHeight: '70vh' } } }}
    >
      {canUndo && (
        <MenuItem onClick={() => { onClose(); onUndo(); }} sx={{ gap: 1.5, fontSize: 14 }}>
          <UndoIcon fontSize="small" sx={{ color: tokens.color.goldLight }} />
          {undoName ? `Undo — back to ${undoName}` : 'Undo — back to no template'}
        </MenuItem>
      )}
      {canUndo && <Divider />}
      {templates.length === 0 && (
        <MenuItem disabled sx={{ fontSize: 14 }}>Loading templates…</MenuItem>
      )}
      {groups.flatMap((g) => [
        <ListSubheader key={`h-${g.id}`} sx={subheaderSx}>{g.label}</ListSubheader>,
        ...g.items.map((t) => {
          const current = t.id === currentId;
          return (
            <MenuItem
              key={t.id}
              selected={current}
              onClick={() => { onClose(); if (!current) onPick(t); }}
              sx={{ gap: 1.5, py: 0.75 }}
            >
              <Typography sx={{ fontSize: 14, color: tokens.color.card, flex: 1 }}>{t.name}</Typography>
              {current && <CheckIcon fontSize="small" sx={{ color: tokens.color.goldLight }} />}
            </MenuItem>
          );
        }),
      ])}
    </Menu>
  );
}
